import { useCallback, useEffect } from 'react';
import { useMutation } from '@apollo/react-hooks';
import { isBefore } from 'date-fns';
import {
  getFromStorage,
  setInStorage,
  removeFromStorage,
} from '../util/storage-utils';
import gql from 'not-graphql-tag';
import useAuth from './useAuth';
import {
  LoginWithTrustedDeviceResponse,
  MutationCreateTrustedDeviceTokenArgs,
  MutationLoginWithTrustedDeviceArgs,
  TrustedDeviceTokenResponse,
} from '../__generated__/pge-types';
import FingerprintJS from '@fingerprintjs/fingerprintjs';
import { UAParser } from 'ua-parser-js';

// Initialize an agent at application startup.
const fpPromise = FingerprintJS.load();

export const TRUSTED_DEVICES_STORAGE_KEY = 'trustedDeviceTokens';

type TrustedDeviceToken = {
  email: string;
  expiresAt: string;
  token: string;
};

const CREATE_TRUSTED_DEVICE_TOKEN = gql`
  mutation createTrustedDeviceToken($input: TrustedDeviceInput!) {
    createTrustedDeviceToken(input: $input) {
      token
      expiresAt
    }
  }
`;

const LOGIN_WITH_TRUSTED_DEVICE = gql`
  mutation loginWithTrustedDevice($input: LoginWithTrustedDeviceInput!) {
    loginWithTrustedDevice(input: $input) {
      customToken
      isVerificationFailed
      isExpired
    }
  }
`;

function getDeviceType() {
  const parser = new UAParser();
  const result = parser.getResult();
  const browser = result?.browser?.name || 'Unknown browser';
  const os = result?.os?.name || 'Unknown OS';
  return `${browser} on ${os}`;
}

export function useTrustedDevice() {
  const [createTrustedDeviceToken] = useMutation<
    { createTrustedDeviceToken: TrustedDeviceTokenResponse },
    MutationCreateTrustedDeviceTokenArgs
  >(CREATE_TRUSTED_DEVICE_TOKEN);
  const [loginWithTrustedDevice] = useMutation<
    { loginWithTrustedDevice: LoginWithTrustedDeviceResponse },
    MutationLoginWithTrustedDeviceArgs
  >(LOGIN_WITH_TRUSTED_DEVICE);

  const authService = useAuth();
  const signInWithCustomToken = authService.signInWithCustomToken.bind(
    authService,
  );

  const getFingerprint = async () => {
    const fp = await fpPromise;
    const result = await fp.get();
    return result.visitorId;
  };

  const getTokens = useCallback((): TrustedDeviceToken[] => {
    const tokens = getFromStorage(TRUSTED_DEVICES_STORAGE_KEY);

    return tokens ? JSON.parse(tokens) : [];
  }, []);

  const setTokens = useCallback((tokens: TrustedDeviceToken[]) => {
    if (tokens.length === 0) {
      removeFromStorage(TRUSTED_DEVICES_STORAGE_KEY);
    } else {
      setInStorage(TRUSTED_DEVICES_STORAGE_KEY, JSON.stringify(tokens));
    }
  }, []);

  useEffect(() => {
    cleanExpiredTokens();
  }, []);

  const upsertToken = useCallback(
    (email: string, expiresAt: string, token: string) => {
      const tokens = getTokens();
      const index = tokens.findIndex(t => t.email === email);

      if (index !== -1) {
        tokens[index] = { email, expiresAt, token };
      } else {
        tokens.push({ email, expiresAt, token });
      }

      setTokens(tokens);
    },
    [getTokens, setTokens],
  );

  const cleanExpiredTokens = useCallback(() => {
    const tokens = getTokens();
    const validTokens = tokens.filter(
      token => !isBefore(new Date(token.expiresAt), new Date()),
    );
    setTokens(validTokens);
  }, [getTokens, setTokens]);

  const registerDevice = useCallback(
    async (email: string): Promise<void> => {
      const fingerprint = await getFingerprint();

      const { data } = await createTrustedDeviceToken({
        variables: {
          input: {
            deviceName: 'default',
            deviceType: getDeviceType(),
            fingerprint,
          },
        },
      });

      const { token, expiresAt } = data?.createTrustedDeviceToken || {};

      if (token && expiresAt) {
        upsertToken(email, expiresAt, token);
      }
    },
    [createTrustedDeviceToken, upsertToken],
  );

  const loginWithTrustedDeviceToken = useCallback(
    async (email: string): Promise<boolean> => {
      const fingerprint = await getFingerprint();

      const tokens = getTokens();
      const trusted = tokens.find(t => t.email === email);

      if (!trusted) {
        return false;
      }

      const isExpired = isBefore(new Date(trusted.expiresAt), new Date());

      if (isExpired) {
        return false;
      }

      try {
        const { data } = await loginWithTrustedDevice({
          variables: {
            input: {
              email,
              fingerprint,
              token: trusted.token,
            },
          },
        });

        const customToken = data?.loginWithTrustedDevice?.customToken;
        if (customToken) {
          await signInWithCustomToken(customToken);
          return true;
        } else {
          return false;
        }
      } catch (e) {
        return false;
      }
    },
    [getTokens, loginWithTrustedDevice],
  );

  const removeCurrentDeviceFromTrustedList = useCallback(
    async (email: string) => {
      const tokens = getTokens();
      const validTokens = tokens.filter(token => token.email !== email);
      setTokens(validTokens);
    },
    [getTokens, setTokens],
  );

  const isTrustedDevice = useCallback(
    (email: string) => {
      const tokens = getTokens();

      const trusted = tokens.find(
        t => t.email === email && isBefore(new Date(), new Date(t.expiresAt)),
      );
      return !!trusted;
    },
    [getTokens],
  );

  return {
    registerDevice,
    loginWithTrustedDeviceToken,
    removeCurrentDeviceFromTrustedList,
    isTrustedDevice,
  };
}
